import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * "Active patient" memory. Records the patient detail route the clinician is
 * currently viewing so the Patients nav can return them to that patient (and
 * the exact tab) after a detour to Profile / Clinicians / Resources — instead
 * of dropping them back on the patient list.
 *
 * Per-device, like the other UI prefs. Cleared by the rail's "All patients"
 * link (see PatientRail), which is the explicit "show me the list" action.
 */

const STORAGE_KEY = 'ui.activePatientPath';

// A patient DETAIL route: /patients/:id[/...]. Excludes the list (/patients).
const PATIENT_DETAIL_RE = /^\/patients\/\d+(?:\/|$)/;

/** The stored active-patient location (pathname + search), or null. */
export const readActivePatientPath = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw && PATIENT_DETAIL_RE.test(raw) ? raw : null;
  } catch {
    return null;
  }
};

const writeActivePatientPath = (path: string) => {
  try {
    localStorage.setItem(STORAGE_KEY, path);
  } catch {
    /* private mode / quota — ignore */
  }
};

/** Forget the active patient — Patients nav then falls back to the list. */
export const clearActivePatient = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Records the current location whenever it's a patient detail route. Mount once
 * at the layout level so every patient page is captured.
 */
export const useTrackActivePatient = (): void => {
  const location = useLocation();
  useEffect(() => {
    if (PATIENT_DETAIL_RE.test(location.pathname)) {
      writeActivePatientPath(location.pathname + location.search);
    }
  }, [location.pathname, location.search]);
};
